import { useState, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ContentCardWithInput,
  InputText,
  InputTextarea,
} from '@teo/components';
import { Theme } from '@teo/components/icons';
import SettingsLP from './components/SettingsLP';
import { axios } from '@/axios';
import { isEmpty } from '@/util';
import { config } from '@/config';
import ThemeActionLP from './components/ThemeActionLP';
import CoursesList from '../AddContent/CoursesList';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { useGet } from '@/query/fetchHooks';
import { useMediaQuery } from 'react-responsive';

const ThemeSettings = ({
  learningpathId,
  nodeSelected,
  handleChangeNode,
  handleRemoveNode,
  setNodes,
  nodes,
  setEdges,
  edges,
  categories,
  allCategories,
}) => {
  const { t } = useTranslation();

  const { auth } = useContext(AuthContext);
  const authUser = auth?.user;

  const [titleTheme, setTitleTheme] = useState(nodeSelected?.data?.title);
  const [descriptionTheme, setDescriptionTheme] = useState('');

  const [isOpenListCoursesModal, setIsOpenListCoursesModal] = useState(false);
  const [selectedCourseType, setSelectedCourseType] = useState('course');
  const [themeDetail, setThemeDetail] = useState(null);

  const basePath = window.location.pathname;

  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  useGet(`/learningpath/${nodeSelected?.data?.nodeID}`, {
    retry: false,
    onSuccess: (res) => {
      setThemeDetail(res?.data || undefined);
      setDescriptionTheme(res?.data?.lpDescription || '');
    },
  });

  const save = async () => {
    const changes = {};
    if (titleTheme !== nodeSelected?.data?.title) {
      changes.lpName = titleTheme;
    }
    if (descriptionTheme !== themeDetail?.lpDescription) {
      changes.lpDescription = descriptionTheme;
    }
    const promises = [];
    if (!isEmpty(changes)) {
      promises.push(
        axios.put(
          `${config.backend}/learningpath/${nodeSelected?.data?.nodeID}`,
          changes
        )
      );
    }
  };

  const habdleChangeTitle = (value) => {
    setTitleTheme(value);
    setNodes(
      nodes?.map((node) =>
        node.id === nodeSelected.id
          ? { ...node, data: { ...node.data, title: value } }
          : node
      )
    );
  };

  const habdleChangeDescription = (value) => {
    setDescriptionTheme(value);
    setNodes(
      nodes?.map((node) =>
        node.id === nodeSelected.id
          ? { ...node, data: { ...node.data, description: value } }
          : node
      )
    );
  };

  const createNodeTheme = async (createNode, type) => {
    const nedesTheme =
      nodes?.find((el) => el?.id === nodeSelected?.id)?.data?.child || [];
    const allRoots = new Set(nedesTheme.flatMap((obj) => obj.roots));
    const lastNode = nedesTheme.find((obj) => !allRoots.has(obj.id));

    const newNode = await axios.post(`${config.backend}/learningpathnodes`, {
      learningpathId: nodeSelected?.data?.nodeID,
      objectId: createNode?.id,
      type: type,
      subType: createNode?.courseType || createNode?.trainingType,
      x: 0,
      y: nedesTheme?.length > 0 ? lastNode?.y + 200 : 200,
      roots: [
        nedesTheme?.length > 0 ? lastNode?.id : nodeSelected?.data?.nodeID,
      ],
      rootDescriptions: [`/d/b`],
      mandatory: true,
      blocking: true,
      notCompleted: true,
    });

    const createNodeId = newNode?.data?.id;
    if (createNodeId) {
      const updateNodes = nodes?.map((nds) =>
        nds?.data?.nodeID === nodeSelected?.data?.nodeID
          ? {
              ...nds,
              data: {
                ...nds?.data,
                child: nds?.data?.child?.concat({
                  ...newNode?.data,
                  name: createNode?.trainingName || createNode?.courseName,
                  picture: createNode?.picture || createNode?.courseImage,
                }),
              },
            }
          : nds
      );
      setNodes(updateNodes);
      setIsOpenListCoursesModal(false);
    }
  };
  const handleAddCourseTheme = async (activeCourse) => {
    if (nodeSelected && activeCourse) {
      const newCourseResponse = await axios.post(`${config.backend}/courses`, {
        courseType: activeCourse?.publicationCourseType,
        coursecontentId: activeCourse?.id,
        courseName: activeCourse?.name,
        courseDescription: activeCourse?.description,
        courseImage: activeCourse?.image ? activeCourse?.image : null,
        correctionType: 'immediate',
      });
      const newCourse = newCourseResponse.data;

      if (newCourse?.id) {
        await axios.post(`${config.backend}/courseinfos`, {
          courseId: newCourse?.id,
        });
        // await axios.post(`${config.backend}/membercourses`, {
        //   courseId: newCourse?.id,
        //   organisationId: auth?.user?.activeOrganisationId,
        // });
        createNodeTheme(newCourse, 'course');
      }
    }
  };
  const handleAddTrainingTheme = async (type) => {
    if (nodeSelected) {
      const newTrainingResponse = await axios.post(
        `${config.backend}/trainings`,
        {
          organisationId: authUser?.activeOrganisationId,
          learningpathId: nodeSelected?.data?.nodeID,
          trainingName: type,
          trainingType: type,
          typeOfTraining:
            type === 'milestone'
              ? 'motivator'
              : type === 'evaluation'
              ? 'internal'
              : 'external',
        }
      );
      const newTraining = newTrainingResponse.data;

      if (newTraining?.id) {
        createNodeTheme(newTraining, 'training');
      }
    }
  };

  return (
    <div
      className={`flex h-full max-h-[calc(100vh_-_150px)] flex-col overflow-hidden ${
        isMobile && 'min-h-[calc(100vh_-_126px)]'
      }`}
    >
      <div className="flex h-full max-h-[calc(100vh_-_200px)] flex-col overflow-y-auto">
        <ContentCardWithInput icon={Theme} color="dark_blue">
          <div className="pt-4">
            <InputText
              value={titleTheme}
              onChange={(e) => habdleChangeTitle(e.currentTarget.value)}
              className="mb-2"
              onBlur={save}
            />
            <InputTextarea
              placeholder={t('pages.learning_path.settings.theme_description')}
              onChange={(event) => habdleChangeDescription(event.target.value)}
              value={descriptionTheme}
              minRows={1}
              customStyles="bg-white inputWhiteBg  text-sm px-[6px]"
              onBlur={save}
            />
          </div>
        </ContentCardWithInput>
        <ThemeActionLP
          nodeSelected={nodeSelected}
          authUser={authUser}
          setSelectedCourseType={setSelectedCourseType}
          setIsOpenListCoursesModal={setIsOpenListCoursesModal}
          handleAddTrainingTheme={handleAddTrainingTheme}
        />
        <SettingsLP
          learningpathId={learningpathId}
          nodeSelected={nodeSelected}
          handleChangeNode={handleChangeNode}
          edges={edges}
          setEdges={setEdges}
          nodes={nodes}
          setNodes={setNodes}
          themeDetail={themeDetail}
          setThemeDetail={setThemeDetail}
          categories={categories}
          allCategories={allCategories}
        />
      </div>

      <div className="mt-auto flex flex-col gap-2 border-t px-5 py-5 shadow-[0px_7px_30px_-9px_grey]">
        <Button
          variant="outline"
          className=""
          onClick={() => {
            window.open(
              `${basePath}/theme/${nodeSelected?.data?.nodeID}/preview`,
              '_blank'
            );
          }}
        >
          {t('pages.learning_path.view_theme')}
        </Button>
        <Button
          variant="ghost"
          className="!text-error-04"
          onClick={() => handleRemoveNode(nodeSelected)}
        >
          {t('pages.learning_path.delete_theme')}
        </Button>
      </div>
      {isOpenListCoursesModal && (
        <CoursesList
          closeModal={() => setIsOpenListCoursesModal(false)}
          handleAddCourse={handleAddCourseTheme}
          courseType={selectedCourseType}
        />
      )}
    </div>
  );
};

export default ThemeSettings;
